import type { PresetKind, AssetKind } from "../../../features/admin/api/adminApi";

/**
 * Pure helpers behind the admin preset panel.
 *
 * Split out of AdminPresets.tsx so they can be exercised without a browser or a
 * saas_owner session — reaching the page itself needs a real login, and these
 * are the parts that fail SILENTLY when wrong. Checked by verify:presets.
 */

/** The kinds that form a browsable library, in the order the editor shows them.
 *  `fx` and `look` are workspace save-kinds, not curated categories. */
export const LIBRARY_KINDS: PresetKind[] = [
  "env", "gemEnv", "background", "ground", "stage", "scene", "pose", "lut", "onBody",
  // the five material categories the reference uses
  "metal", "gem", "ceramic", "pearl", "other",
];

export const KIND_LABEL: Record<string, string> = {
  env: "Environment", gemEnv: "Gem environment", background: "Background", ground: "Ground",
  stage: "Model stage", scene: "Scene (vjson)", pose: "Camera pose", lut: "LUT",
  onBody: "On-body image", metal: "Metal", gem: "Gem", ceramic: "Ceramic", pearl: "Pearl",
  other: "Other material", fx: "Post FX", look: "Look",
};

/** Which file types each kind expects, for the picker's `accept`. */
export const KIND_ACCEPT: Record<string, string> = {
  env: ".hdr,.exr", gemEnv: ".hdr,.exr", background: ".svg,.png,.jpg,.jpeg,.webp",
  ground: ".json", stage: ".glb,.gltf", scene: ".vjson,.json", pose: ".json",
  lut: ".cube", onBody: ".jpg,.jpeg,.png,.webp", metal: ".pmat", gem: ".dmat",
  ceramic: ".pmat", pearl: ".pmat",
  // The reference's "other" bucket holds BOTH file types, which is why apply
  // routes on the file extension rather than on the category.
  other: ".pmat,.dmat",
};

/**
 * File extension → VaultAsset kind.
 *
 * Load-bearing: the server defaults a missing `assetKind` to 'doc', which is
 * wrong for every library asset and makes the asset-kind filter useless. The
 * upload still succeeds, so nothing would tell you it happened.
 */
export function assetKindFor(filename: string): AssetKind {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  if (ext === "hdr") return "hdr";
  if (ext === "exr") return "exr";
  if (ext === "glb" || ext === "gltf") return "glb";
  if (ext === "vjson") return "vjson";
  // Ground configs and camera poses are plain .json — a real asset type in the
  // reference library (26 of theirs), not a generic document.
  if (ext === "json") return "json";
  if (ext === "pmat") return "pmat";
  if (ext === "dmat") return "dmat";
  if (ext === "cube") return "lut";
  if (["png", "jpg", "jpeg", "webp", "svg", "gif"].includes(ext)) return "image";
  return "doc";
}

/*
 * Uploads travel as base64 inside a JSON body against express.json({limit:'40mb'})
 * (server/index.ts) and base64 inflates by ~4/3. Refuse early with a real message
 * rather than letting a large EXR come back as an opaque 413.
 */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

/** Comma-separated tag input → the array the API stores. */
export const parseTags = (text: string): string[] =>
  text.split(",").map((t) => t.trim()).filter(Boolean);
